import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  ButtonGroup,
  Form,
  InputGroup,
  Dropdown,
  Table,
  Pagination,
  Badge,
  OverlayTrigger,
  Tooltip,
  Stack,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { selectTokens } from "../redux/userSlice";
import LoadingAnimation from "./LoadingAnimation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faUserPlus,
  faUserMinus,
  faTimes,
  faDownload,
  faSpinner,
  faFile,
} from "@fortawesome/free-solid-svg-icons";
import SaleRegistrationModal from "./SaleRegistrationModal";
import * as XLSX from "xlsx";
import { downloadTaxSaleBidderRegistrationCard } from "../redux/registrationSlice";
import { useDispatch } from "react-redux";

const SaleRegistrationsComponent = () => {
  const dispatch = useDispatch();

  const [sales, setSales] = useState([]);
  const [saleDate, setSaleDate] = useState("");
  const [filteredSales, setFilteredSales] = useState([]);
  const [saleSearchText, setSaleSearchText] = useState("");
  const [registrationSearchText, setRegistrationSearchText] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [salesPerPage] = useState(10);
  const tokens = useSelector(selectTokens);
  const [salesWithDates, setSalesWithDates] = useState([]);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const apiKey = process.env.REACT_APP_SERVER_API_KEY;

  useEffect(() => {
    fetchAllSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSalesWithDates([
      ...new Set(
        sales
          .filter((sale) => sale.sale_date)
          .map((sale) => sale.sale_date)
          .reverse()
      ),
    ]);
  }, [sales]);

  useEffect(() => {
    setCurrentPage(1);
  }, [saleSearchText, sortField, sortDirection]);

  const fetchAllSales = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/sales`, {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
          "x-api-key": apiKey,
        },
      });
      setSales(response.data);
      setFilteredSales(response.data);
    } catch (error) {
      console.error("Error fetching sales:", error);
      alert("Failed to fetch sales.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let updated = [...sales];

    if (saleDate) {
      updated = updated.filter((s) => s.sale_date === saleDate);
    }

    if (saleSearchText.trim()) {
      const searchLower = saleSearchText.trim().toLowerCase();
      updated = updated.filter(
        (s) =>
          (s.sale_name && s.sale_name.toLowerCase().includes(searchLower)) ||
          (s.sale_id && s.sale_id.toString().includes(searchLower))
      );
    }

    if (registrationSearchText.trim()) {
      const regSearch = registrationSearchText.trim();
      updated = updated.filter((s) =>
        s.registrations.some(
          (r) =>
            r.global_reg_number &&
            r.global_reg_number.toString().includes(regSearch)
        )
      );
    }

    if (sortField) {
      updated.sort((a, b) => {
        let aVal, bVal;
        if (sortField === "sale_name") {
          aVal = a.sale_name || "";
          bVal = b.sale_name || "";
        } else if (sortField === "sale_date") {
          aVal = a.sale_date || "";
          bVal = b.sale_date || "";
        } else if (sortField === "registrations") {
          aVal = a.registrations.length;
          bVal = b.registrations.length;
        } else if (sortField === "sale_id") {
          // Ensure numeric comparison for sale_id
          aVal = parseInt(a.sale_id, 10) || 0;
          bVal = parseInt(b.sale_id, 10) || 0;
        }

        if (sortField === "registrations" || sortField === "sale_id") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        } else {
          return sortDirection === "asc"
            ? aVal.toString().localeCompare(bVal.toString())
            : bVal.toString().localeCompare(aVal.toString());
        }
      });
    }

    setFilteredSales(updated);
    setCurrentPage(1);
  }, [
    sales,
    saleDate,
    saleSearchText,
    registrationSearchText,
    sortField,
    sortDirection,
  ]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const indexOfLastSale = currentPage * salesPerPage;
  const indexOfFirstSale = indexOfLastSale - salesPerPage;
  const currentSales = filteredSales.slice(indexOfFirstSale, indexOfLastSale);

  const handleShowModal = (sale) => {
    setSelectedSale(sale);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    fetchAllSales();
  };

  const handleDownload = (sale) => {
    const data = sale.registrations.map((reg) => ({
      RegistrationNumber: reg.registration_number,
      GlobalRegistrationNumber: reg.global_reg_number,
      Name: `${reg.first_name} ${reg.middle_name || ""} ${
        reg.last_name
      }`.trim(),
      StreetAddress: reg.addresses[0]?.street_address,
      City: reg.addresses[0]?.city,
      State: reg.addresses[0]?.state,
      Zip: reg.addresses[0]?.zip,
      Email: reg.email,
    }));
    if (!data.length) {
      alert("No data to download.");
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    const filename = `${sale.sale_name}_Bidders_${today}.xlsx`;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bidders");
    XLSX.writeFile(wb, filename);
  };

  const handleUnregister = async (userId, saleId) => {
    if (!window.confirm("Are you sure you want to unregister this user?"))
      return;
    setIsLoading(true);
    try {
      await axios.delete(
        `${baseUrl}/sales/user/${userId}/sales/${saleId}/unregister`,
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            "x-api-key": apiKey,
          },
        }
      );
      fetchAllSales();
      alert("User unregistered.");
    } catch (error) {
      console.error("Error unregistering:", error);
      alert("Failed to unregister user.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadRegistrationCardPDF = async (user, sale) => {
    setDownloadLoading((prev) => ({
      ...prev,
      [`${user.user_id}-${sale.sale_id}`]: true,
    }));
    try {
      await dispatch(
        downloadTaxSaleBidderRegistrationCard({ selectedUser: user, sale })
      ).unwrap();
    } catch (error) {
      console.error(
        "Error retrieving tax sale bidder registration document:",
        error
      );
      alert("Failed to retrieve Tax Sale Bidder Registration Document.");
    } finally {
      setDownloadLoading((prev) => ({
        ...prev,
        [`${user.user_id}-${sale.sale_id}`]: false,
      }));
    }
  };

  const RemoveRegistrationToolTip = ({ reg, sale }) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="tooltip-top">Remove Registration</Tooltip>}
    >
      <Button
        size="sm"
        variant="outline-danger"
        className="mb-2"
        onClick={() => handleUnregister(reg.user_id, sale.sale_id)}
      >
        <FontAwesomeIcon icon={faUserMinus} />
      </Button>
    </OverlayTrigger>
  );

  const AddRegistrationToolTip = ({ sale }) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="tooltip-top">Add Registration</Tooltip>}
    >
      <Button
        variant="outline-success"
        className="mb-2 w-100"
        onClick={() => handleShowModal(sale)}
      >
        <FontAwesomeIcon icon={faUserPlus} />
      </Button>
    </OverlayTrigger>
  );

  const DownloadRegistrationToolTip = ({ sale }) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="tooltip-top">Download Registrations</Tooltip>}
    >
      <Button
        variant="outline-secondary"
        className="w-100 mb-2"
        onClick={() => handleDownload(sale)}
      >
        <FontAwesomeIcon icon={faDownload} />
      </Button>
    </OverlayTrigger>
  );

  const DownloadRegistrationDocumentTooltip = ({ reg, sale }) => {
    const loadingKey = `${reg.user_id}-${sale.sale_id}`;
    const isLoading = downloadLoading[loadingKey] || false;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="tooltip-top">
            Download Tax Sale Bidder Registration Card
          </Tooltip>
        }
      >
        <Button
          variant="outline-secondary"
          disabled={isLoading}
          className="w-100 mb-2"
          onClick={() => handleDownloadRegistrationCardPDF(reg, sale)}
        >
          <>
            {!isLoading ? (
              <FontAwesomeIcon icon={faFile} />
            ) : (
              <FontAwesomeIcon icon={faSpinner} spin />
            )}
          </>
        </Button>
      </OverlayTrigger>
    );
  };

  return (
    <div className="sale-reg-container">
      {isLoading ? (
        <LoadingAnimation />
      ) : (
        <>
          <h2 className="mb-3">Manage Sale Registrations</h2>

          <InputGroup className="mb-3">
            <Form.Control
              placeholder="Search by Sale Name or ID..."
              value={saleSearchText}
              onChange={(e) => setSaleSearchText(e.target.value)}
            />
          </InputGroup>

          <InputGroup className="mb-3">
            <Form.Control
              placeholder="Search by Registration Number..."
              value={registrationSearchText}
              onChange={(e) => setRegistrationSearchText(e.target.value)}
            />
          </InputGroup>

          <div className="mb-2">
            {saleDate && (
              <Badge bg="info" className="me-2">
                Date: {saleDate}
              </Badge>
            )}
            {saleSearchText && (
              <Badge bg="info">Search: "{saleSearchText}"</Badge>
            )}
            {registrationSearchText && (
              <Badge bg="info" className="ms-2">
                Registration #: "{registrationSearchText}"
              </Badge>
            )}
            {sortField && (
              <Badge bg="secondary" className="ms-2">
                Sort: {sortField.replace("_", " ")} ({sortDirection})
              </Badge>
            )}
          </div>

          <Dropdown className="mb-3 d-inline-block">
            <Dropdown.Toggle variant="primary">
              <FontAwesomeIcon icon={faFilter} />{" "}
              {saleDate ? `Date: ${saleDate}` : "Filter by Sale Date"}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ height: "15em", overflowY: "scroll" }}>
              {salesWithDates.length > 0
                ? salesWithDates.map((sale_date) => {
                    return (
                      <Dropdown.Item
                        key={sale_date}
                        onClick={() => setSaleDate(sale_date)}
                      >
                        {sale_date}
                      </Dropdown.Item>
                    );
                  })
                : null}
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => setSaleDate("")}>
                <FontAwesomeIcon icon={faTimes} /> Clear Date Filter
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown className="mb-3 ms-2 d-inline-block">
            <Dropdown.Toggle variant="secondary">Sort By</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setSortField("sale_id")}>
                Sale ID
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortField("sale_name")}>
                Sale Name
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortField("sale_date")}>
                Sale Date
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortField("registrations")}>
                Registrations
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => setSortField("")}>
                <FontAwesomeIcon icon={faTimes} /> Clear Sorting
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            variant={
              sortDirection === "asc" ? "outline-success" : "outline-danger"
            }
            className="ms-2"
            onClick={() =>
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
            }
            disabled={!sortField}
            style={{ backgroundColor: "#ccc" }}
          >
            {sortDirection === "asc" ? "Ascending" : "Descending"}
          </Button>

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Sale Name</th>
                <th>Date</th>
                <th>Registrations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentSales.length > 0 ? (
                currentSales.map((sale) => (
                  <tr key={sale.sale_id}>
                    <td>{sale.sale_name}</td>
                    <td>
                      {sale.sale_date
                        ? sale.sale_date.split("T")[0]
                        : "Not Available"}
                    </td>
                    <td>
                      {sale.registrations.length ? (
                        <ul className="list-unstyled">
                          {sale.registrations.map((reg) => (
                            <li key={reg.user_id}>
                              <Stack
                                direction="horizontal"
                                className="justify-content-between align-items-center"
                              >
                                <span>
                                  {reg.email} (Reg No: {reg.global_reg_number})
                                </span>
                                <ButtonGroup>
                                  <RemoveRegistrationToolTip
                                    reg={reg}
                                    sale={sale}
                                  />
                                  <DownloadRegistrationDocumentTooltip
                                    reg={reg}
                                    sale={sale}
                                  />
                                </ButtonGroup>
                              </Stack>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        "No Registrations"
                      )}
                    </td>
                    <td>
                      <ButtonGroup>
                        <AddRegistrationToolTip sale={sale} />
                        <DownloadRegistrationToolTip sale={sale} />
                      </ButtonGroup>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">
                    No sales found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {filteredSales.length > salesPerPage && (
            <div className="text-center mt-3">
              <Pagination className="d-inline-flex">
                <Pagination.First
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                />

                {(() => {
                  const totalPages = Math.ceil(
                    filteredSales.length / salesPerPage
                  );
                  const pages = [];

                  if (totalPages <= 10) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    if (currentPage <= 4) {
                      pages.push(1, 2, 3, 4, 5, "...", totalPages);
                    } else if (currentPage >= totalPages - 2) {
                      pages.push(
                        1,
                        "...",
                        totalPages - 4,
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages
                      );
                    } else {
                      pages.push(
                        1,
                        "...",
                        currentPage - 2,
                        currentPage - 1,
                        currentPage,
                        currentPage + 1,
                        currentPage + 2,
                        "...",
                        totalPages
                      );
                    }
                  }

                  return pages.map((page, index) =>
                    page === "..." ? (
                      <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                    ) : (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => paginate(page)}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  );
                })()}

                <Pagination.Next
                  onClick={() => paginate(currentPage + 1)}
                  disabled={
                    currentPage ===
                    Math.ceil(filteredSales.length / salesPerPage)
                  }
                />
                <Pagination.Last
                  onClick={() =>
                    paginate(Math.ceil(filteredSales.length / salesPerPage))
                  }
                  disabled={
                    currentPage ===
                    Math.ceil(filteredSales.length / salesPerPage)
                  }
                />
              </Pagination>
            </div>
          )}
          {filteredSales.length > 0 && (
            <p style={{ textAlign: "center" }}>
              {(currentPage - 1) * salesPerPage + 1}-
              {Math.min(currentPage * salesPerPage, filteredSales.length)} of{" "}
              {filteredSales.length}
            </p>
          )}

          <SaleRegistrationModal
            isOpen={showModal}
            onClose={handleCloseModal}
            saleId={selectedSale?.sale_id}
            saleDate={selectedSale?.sale_date}
            registeredUsers={selectedSale?.registrations}
          />
        </>
      )}
    </div>
  );
};

export default SaleRegistrationsComponent;
